<template>
  <button
    :type="type"
    class="ksk-btn"
    :class="[`ksk-btn--${variant}`, `ksk-btn--${size}`, { 'ksk-btn--block': block }]"
    :disabled="disabled"
    v-bind="kskSlot(colorSlot || `button_${variant}`)"
    @click="$emit('click', $event)"
  >
    <span v-if="icon" class="material-icons-round ksk-btn__icon">{{ icon }}</span>
    <span class="ksk-btn__label"><slot /></span>
    <span v-if="iconRight" class="material-icons-round ksk-btn__icon">{{ iconRight }}</span>
  </button>
</template>

<script>
import { kskSlot } from "@/config/kioskColorSlots";

// KSK004 — kiosk theme-2 button. Primary 96px / ≥320px wide; outlined and
// muted 80px; text button for low-emphasis actions.
export default {
  name: "KskButton",
  emits: ["click"],
  props: {
    variant: {
      type: String,
      default: "primary",
      validator: (v) => ["primary", "outlined", "muted", "text"].includes(v),
    },
    size: {
      type: String,
      default: "lg",
      validator: (v) => ["lg", "md"].includes(v),
    },
    icon: { type: String, default: "" },
    iconRight: { type: String, default: "" },
    block: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
    type: { type: String, default: "button" },
    // KSK063: colour slot (kioskColorSlots.js); defaults to button_<variant>.
    colorSlot: { type: String, default: "" },
  },
  methods: { kskSlot },
};
</script>

<style scoped>
.ksk-btn {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--ksk-space-2);
  padding: 0 var(--ksk-space-4);
  border: 2px solid transparent;
  border-radius: var(--ksk-radius-pill);
  font-family: var(--ksk-font);
  font-size: var(--ksk-label-size);
  line-height: var(--ksk-label-lh);
  font-weight: 600;
  white-space: nowrap;
  cursor: pointer;
  overflow: hidden;
  transition: transform 80ms ease-out, background-color 80ms ease-out;
  -webkit-tap-highlight-color: transparent;
  user-select: none;
}
.ksk-btn--lg {
  height: 96px;
}
.ksk-btn--md {
  height: 80px;
}
.ksk-btn--primary.ksk-btn--lg {
  min-width: 320px;
}
.ksk-btn--block {
  display: flex;
  width: 100%;
}

/* KSK063: each colour falls back to the theme value unless the merchant set
   the slot's outline / font / accent (kioskColorSlots.js). */
.ksk-btn--primary {
  background: var(--ksk-slot-accent, var(--ksk-primary));
  border-color: var(--ksk-slot-outline, transparent);
  color: var(--ksk-slot-font, var(--ksk-on-primary));
  box-shadow: var(--ksk-shadow-float);
}
.ksk-btn--outlined {
  background: var(--ksk-slot-accent, var(--ksk-surface));
  border-color: var(--ksk-slot-outline, var(--ksk-primary));
  color: var(--ksk-slot-font, var(--ksk-primary));
}
.ksk-btn--muted {
  background: var(--ksk-slot-accent, var(--ksk-surface-muted));
  border-color: var(--ksk-slot-outline, transparent);
  color: var(--ksk-slot-font, var(--ksk-text));
}
.ksk-btn--text {
  background: var(--ksk-slot-accent, transparent);
  border-color: var(--ksk-slot-outline, transparent);
  color: var(--ksk-slot-font, var(--ksk-primary));
  padding: 0 var(--ksk-space-3);
}

/* Pressed: scale 0.97 + 8% tint of the current text colour. */
.ksk-btn::after {
  content: "";
  position: absolute;
  inset: 0;
  background: currentColor;
  opacity: 0;
  pointer-events: none;
}
.ksk-btn:active:not(:disabled) {
  transform: scale(0.97);
}
.ksk-btn:active:not(:disabled)::after {
  opacity: 0.08;
}
.ksk-btn--primary:active:not(:disabled) {
  background: color-mix(in srgb, var(--ksk-slot-accent, var(--ksk-primary)) 78%, #000000);
}

.ksk-btn:disabled {
  background: var(--ksk-surface-muted);
  border-color: transparent;
  color: var(--ksk-text-secondary);
  box-shadow: none;
  cursor: not-allowed;
}

.ksk-btn__icon {
  font-size: 32px;
  line-height: 1;
}
</style>
